import { Highlight } from 'prism-react-renderer'
import React, { Component, useState } from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'
import styled, { css } from 'styled-components'

import { defaultTheme as theme } from '../../../../theme'
import { stringify } from '../../../../utils/stringify'
import { mobile } from '../../../../utils/styles/media'
import childrenToString from './childrenToString'

type PreProps = React.ComponentPropsWithoutRef<typeof PreWrapper> & { copy?: boolean; playgroundLink?: string }

const getPreSettings = (children: any) => {
  let copy = false
  let lineNumbers = false
  let lang: any = ''
  if (children && children.props && children.props.className) {
    const split = children.props.className.split(' ')
    lang = split[0].replace(/language-/, '')
  } else if (children && children.props && children.props.props && children.props.props.className) {
    const settings = children.props.props
    lang = settings.className.replace(/language-/, '')
  }

  if (children && children.props && children.props.metastring) {
    copy = children.props.metastring.includes('copy')
    lineNumbers = children.props.metastring.includes('lineNumbers')
  } else if (children && children.props && children.props.props && children.props.props.metastring) {
    copy = children.props.metastring.includes('copy')
    lineNumbers = children.props.metastring.includes('lineNumbers')
  }

  return { copy, lineNumbers, lang }
}

function cleanTokens(tokens: any) {
  const tokensLength = tokens.length

  if (tokensLength === 0) {
    return tokens
  }
  const lastToken = tokens[tokensLength - 1]

  if (lastToken.length === 1 && lastToken[0].empty) {
    return tokens.slice(0, tokensLength - 1)
  }
  return tokens
}

const Pre = ({ children, copy, playgroundLink }: PreProps) => {
  const settings = getPreSettings(children)
  const showCopy = copy || settings.copy
  const codeEl = React.useRef<any>(null)
  const preEl = React.useRef<any>(null)
  const [loadedLines, setLoadedLines] = React.useState<boolean>(false)
  // const [scrollable, setScrollable] = useState<any>(null)
  const code = stringify(children)
  const [debugState, setDebugState] = useState<string>('')
  const [LineNumbers, setLineNumbers] = useState(false)

  React.useEffect(() => {
    if (codeEl.current !== null && preEl.current !== null) {
      if (debugState.length === 0)
        setDebugState(
          `${codeEl.current.getBoundingClientRect().width},${preEl.current.getBoundingClientRect().width - 44}`,
        )
    }
  }, [])

  return (
    <Highlight code={code} language={settings.lang}>
      {({ className, style, tokens, getLineProps, getTokenProps }) => (
        <CodeHighlightWrapper>
          <PreWrapper
            className={`${LineNumbers && `line-numbers`} ${
              parseInt(debugState.split(',')[0]) <= parseInt(debugState.split(',')[1]) && `not-scrollable`
            }`}
            style={{ ...style, backgroundColor: theme.colors.gray[200] }}
            ref={preEl}
          >
            <code ref={codeEl} style={{ opacity: loadedLines ? 1 : 0, transition: 'opacity 100ms ease-in-out' }}>
              {cleanTokens(tokens).map((line: any, i: number) => {
                let lineClass = {}

                let isDiff = false
                let diffSymbol = ''
                let margins = { margin: '0 -29px', padding: '0 13px' }
                if (line[0] && line[0].content.length && line[0].content[0] === '+') {
                  lineClass = { backgroundColor: 'rgba(76, 175, 80, 0.2)', ...margins }
                  diffSymbol = '+'
                  setLineNumbers(true)
                  isDiff = true
                } else if (line[0] && line[0].content === '' && line[1] && line[1].content[0] === '|') {
                  lineClass = { backgroundColor: theme.colors.gray[300], ...margins }
                  diffSymbol = ` `
                  isDiff = true
                } else if (
                  line[0] &&
                  line[0].content.length &&
                  line[0].content[0] === '-' &&
                  line[1] &&
                  line[1].content.length &&
                  line[1].content[0] !== '-'
                ) {
                  lineClass = { backgroundColor: 'rgba(244, 67, 54, 0.2)', ...margins }
                  diffSymbol = '-'
                  setLineNumbers(true)
                  isDiff = true
                } else if (line[0] && line[0].content === '' && line[1] && line[1].content === '+') {
                  lineClass = { backgroundColor: 'rgba(76, 175, 80, 0.2)', ...margins }
                  diffSymbol = '+'
                  setLineNumbers(true)
                  isDiff = true
                } else if (
                  line[0] &&
                  line[0].content === '' &&
                  line[1] &&
                  line[1].content === '-' &&
                  line[2] &&
                  line[2].content !== '-'
                ) {
                  lineClass = { backgroundColor: 'rgba(244, 67, 54, 0.2)', ...margins }
                  diffSymbol = '-'
                  setLineNumbers(true)
                  isDiff = true
                }
                const lineProps = getLineProps({ line, key: i })

                lineProps.style = { ...lineClass }

                lineProps.style = lineClass

                let splitToken
                if (i === cleanTokens(tokens).length - 1) setLoadedLines(true)
                return (
                  <div {...lineProps} key={line + i}>
                    <span
                      key={line + i}
                      className={`${!isDiff && LineNumbers ? 'line-counting' : 'hide-line-counting'}`}
                    >
                      {isDiff && diffSymbol}
                    </span>
                    {line.map((token: any, key: number) => {
                      if (isDiff) {
                        if (
                          (key === 0 || key === 1) &&
                          (token.content.charAt(0) === '+' ||
                            token.content.charAt(0) === '-' ||
                            token.content.charAt(0) === '|')
                        ) {
                          if (token.content.length > 1) {
                            splitToken = {
                              types: ['template-string', 'string'],
                              content: token.content.slice(1),
                            }
                            return (
                              <React.Fragment key={token + key}>
                                <span {...getTokenProps({ token: splitToken, key })} />
                              </React.Fragment>
                            )
                          } else {
                            return (
                              <span
                                {...getTokenProps({
                                  token: {
                                    ...token,
                                    content:
                                      settings.lang === 'yml' && diffSymbol === '+'
                                        ? token.content
                                        : token.content.slice(1),
                                  },
                                  key,
                                })}
                                style={{ userSelect: 'none', color: '' }}
                              />
                            )
                          }
                        }
                      }
                      return <span {...getTokenProps({ token, key })} style={{ color: '' }} />
                    })}
                  </div>
                )
              })}
            </code>
            {playgroundLink && (
              <PlaygroundLink href={playgroundLink} target="_blank">
                <ButtonSmall>Open in Playground</ButtonSmall>
              </PlaygroundLink>
            )}

            {showCopy && (
              <AbsoluteCopyButton>
                <CopyButton text={childrenToString(children)} />{' '}
              </AbsoluteCopyButton>
            )}
          </PreWrapper>
        </CodeHighlightWrapper>
      )}
    </Highlight>
  )
}

export default Pre

const CodeHighlightWrapper = styled.div`
  position: relative;
`

const PreWrapper = styled.pre<{ option?: boolean; selected?: boolean }>`
  background-color: ${theme.colors.gray[200]} !important;
  border-radius: ${(p) => p.theme.radius}px;
  padding: 20px;
  font-size: 14px !important;
  line-height: 24px !important;
  color: ${(p) => p.theme.primary};
  margin-top: 32px !important;
  margin-bottom: 32px !important;
  overflow-x: visible;
  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
    background-color: transparent;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: rgba(0, 0, 0, 0.2);
  }
  &::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: transparent;
  }
  &::-webkit-scrollbar-corner {
    background-color: transparent;
    border-color: transparent;
  }
  &.not-scrollable {
    overflow-x: hidden;
    code {
      display: inline;
    }
  }
  &:last-child {
    margin-bottom: 0 !important;
  }

  margin: 0.5em 0;
  overflow: auto;
  padding: 1em;

  code {
    text-shadow: none !important;
    display: block;
    width: fit-content;
    font-family: ${(p) => p.theme.fontStackMono} !important;
  }

  ${mobile(css`
    margin-top: 20px !important;
    font-size: 12px;
    margin-bottom: 20px !important;
  `)};

  &.line-numbers {
    counter-reset: lineNumbers;
    padding-left: 30px !important;
  }
  .line-counting {
    position: absolute;
    width: 1.3em;
    text-align: right;
    left: 0.3em;
  }

  .line-counting:before {
    counter-increment: lineNumbers;
    content: counter(lineNumbers);
    color: ${(p) => p.theme.primaryLight3};
  }
  .hide-line-counting {
    user-select: none;
    color: black;
  }
  .hide-line-counting:before {
    counter-increment: lineNumbers;
    content: '';
  }
  .hide-line-counting:after {
    content: '';
    margin: 0 0.3em;
  }

  .line-highlight {
    transform: translateY(6px);
    background: rgba(75, 138, 167, 0.07);
  }
`
const AbsoluteCopyButton = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  > div {
    right: -8px;
    top: -2px;
  }
`

const PlaygroundLink = styled.a`
  position: absolute;
  top: 16px;
  right: 67px;
`

const ButtonSmall = styled.div`
  cursor: pointer;

  background: ${(p) => p.theme.primaryLight2};
  box-shadow: 0px 2px 4px rgba(8, 35, 51, 0.05);
  border-radius: 4px;

  font-family: ${theme.fonts.text};
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  padding: 5px;
  padding-bottom: 6px;

  color: #ffffff;
  line-height: 1;
  text-shadow: none;
`

const CopyButton = ({ text = '', className = '' }) => (
  <Copy text={text} className={className}>
    <ButtonSmall>Copy</ButtonSmall>
  </Copy>
)

class Copy extends Component<{ text: string; className: string; color?: string }> {
  state = { copied: false }
  copyTimer: number | null = null

  componentWillUnmount() {
    if (this.copyTimer) clearTimeout(this.copyTimer)
  }

  render() {
    const { text, className, color = 'rgba(42,126,210,1)' } = this.props
    const combine = /(^[+])|(.*^[-].*)/gm

    return (
      <CopyToClipboard text={text.replace(combine, '')} onCopy={this.onCopy}>
        <CopyComponent className={className}>
          {this.state.copied && (
            <div className="indicator" style={{ color }}>
              Copied
            </div>
          )}
          {this.props.children}
        </CopyComponent>
      </CopyToClipboard>
    )
  }

  onCopy = () => {
    this.setState({ copied: true })
    this.copyTimer = window.setTimeout(() => this.setState({ copied: false }), 500)
  }
}

const CopyComponent = styled.div`
  & {
    position: relative;
    cursor: pointer;
    display: inline-block;
  }
  @keyframes copying {
    0% {
      opacity: 0;
      transform: translate(-50%, 0);
    }

    50% {
      opacity: 1;
    }

    100% {
      opacity: 0;
      transform: translate(-50%, 50px);
    }
  }
  .indicator {
    position: absolute;
    top: 20px;
    left: 50%;
    transform: translate(-50%, 0);
    animation: copying 700ms linear;
  }
`
